// Tiny in-house i18n. Three languages — English, Spanish, French.
// Translations cover UI chrome (buttons, headers, microcopy). Tile labels
// remain user-editable so caregivers can speak in any dialect they want.

export const LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English", ttsLang: "en-US", flag: "🇺🇸" },
  { code: "es", label: "Spanish", nativeLabel: "Español", ttsLang: "es-ES", flag: "🇪🇸" },
  { code: "fr", label: "French", nativeLabel: "Français", ttsLang: "fr-FR", flag: "🇫🇷" },
];

export function ttsLangFor(code) {
  return LANGUAGES.find((l) => l.code === code)?.ttsLang || "en-US";
}

const STRINGS = {
  en: {
    // Board chrome
    "board.tap_hint": "Tap a tile to speak · Build a sentence above",
    "board.empty.favorites": "Mark tiles as favorites to see them here.",
    "board.empty.most_used": "Tap some tiles and the most-used will appear here.",
    "board.empty.category": "Tap Edit in the top right to add tiles.",
    "board.empty.title": "Nothing here yet",
    "board.add_tile": "Add tile",
    "board.edit": "Edit",
    "board.done": "Done",
    "board.first_word.toast": "🎉 First word! Way to go!",
    "board.first_word.speak": "Yes! That was awesome!",

    // Sentence strip
    "strip.placeholder": "Tap a tile below to start a sentence…",
    "strip.speak": "Speak",
    "strip.back": "Back",
    "strip.clear": "Clear",
    "strip.suggested": "Suggested",

    // Categories rail
    "cat.favorites": "Favorites",
    "cat.most_used": "Most used",
    "cat.add_category": "Add category",

    // Settings
    "settings.title": "settings",
    "settings.profile": "Profile",
    "settings.name": "Name",
    "settings.avatar": "Avatar",
    "settings.color": "Color",
    "settings.voice": "Voice",
    "settings.voice.preview": "Preview voice",
    "settings.voice.speed": "Speed",
    "settings.voice.pitch": "Pitch",
    "settings.tile_size": "Tile size",
    "settings.language": "Language & voice",
    "settings.language.help": "Pick the language used for the app's buttons and the voice used to speak tiles. Tile labels stay exactly as you wrote them — change them anytime.",
    "settings.pin.title": "Caregiver lock",
    "settings.pin.help": "Set a 4-6 digit PIN so only you can enter Edit mode on the board.",
    "settings.pin.set": "Set PIN",
    "settings.pin.change": "Change PIN",
    "settings.pin.remove": "Remove PIN",
    "settings.ai.title": "AI suggestions",
    "settings.ai.help": "Show smart \"suggested next word\" pills above the sentence strip, powered by Gemini 3 Flash.",
    "settings.ai.toggle": "Enable AI suggestions",
    "settings.backup.title": "Backup & restore",
    "settings.backup.help": "Download this entire board (every tile, photo, and recording) as a single JSON file.",
    "settings.backup.export": "Export this board",
    "settings.backup.import": "Import board",
    "settings.reset.title": "Reset board",
    "settings.reset.help": "Wipe this board and start over with the latest starter vocabulary.",
    "settings.reset.button": "Reset to default vocabulary",
    "settings.save": "Save changes",
    "settings.saved": "Saved ✓",
    "settings.donate.title": "Pay it forward",
    "settings.donate.help": "Sponsor a family who can't afford a paid plan. Pledge now — we'll email you the moment our payment system goes live and you can choose to charge or back out.",
    "settings.donate.email": "Your email",
    "settings.donate.name": "Your name (optional)",
    "settings.donate.amount": "Amount (USD)",
    "settings.donate.families": "Families to sponsor",
    "settings.donate.message": "Note to the families (optional)",
    "settings.donate.submit": "Pledge support",
    "settings.donate.thanks": "Thank you! You'll hear from us when checkout is live.",
    "settings.donate.stat": "{count} supporters · ${pledged} pledged · {families} families covered",

    // Common
    "common.back": "Back",
    "common.save": "Save",
    "common.cancel": "Cancel",

    // Public marketing surface — non-US banner, waitlist, language switcher
    "public.nonus.line1": "Outside the US?",
    "public.nonus.line2": "SonSpeak is launching in the US first. Get on the waitlist and we'll email you the moment we open in your country.",
    "public.nonus.cta": "Join the waitlist →",
    "public.nonus.dismiss": "Dismiss",
    "public.lang.label": "Language",
    "public.waitlist.title": "International waitlist",
    "public.waitlist.subtitle": "SonSpeak is launching in the US first while we finish region-specific privacy work. Drop your email and we'll let you know the day we open in your country.",
    "public.waitlist.name": "Your name",
    "public.waitlist.email": "Email",
    "public.waitlist.country": "Country",
    "public.waitlist.submit": "Add me to the waitlist",
    "public.waitlist.busy": "Adding you…",
    "public.waitlist.success.title": "You're on the list!",
    "public.waitlist.success.body": "We'll email {email} the moment SonSpeak opens in your country. No spam — just the launch announcement.",
    "public.waitlist.success.home": "Back to home",
    "public.waitlist.privacy_note": "One email when we launch in your country. Nothing else, ever.",
    "public.whatsnew.badge": "What's new",
    "public.whatsnew.title": "What's new on SonSpeak",
    "public.whatsnew.subtitle": "Recent shipped updates. Nothing dropped, nothing left behind.",
  },
  es: {
    "board.tap_hint": "Toca una tarjeta para hablar · Construye una frase arriba",
    "board.empty.favorites": "Marca tarjetas como favoritas para verlas aquí.",
    "board.empty.most_used": "Toca tarjetas y las más usadas aparecerán aquí.",
    "board.empty.category": "Toca Editar arriba a la derecha para añadir tarjetas.",
    "board.empty.title": "Aún no hay nada aquí",
    "board.add_tile": "Añadir tarjeta",
    "board.edit": "Editar",
    "board.done": "Listo",
    "board.first_word.toast": "🎉 ¡Primera palabra! ¡Muy bien!",
    "board.first_word.speak": "¡Sí! ¡Eso fue increíble!",

    "strip.placeholder": "Toca una tarjeta para empezar una frase…",
    "strip.speak": "Hablar",
    "strip.back": "Atrás",
    "strip.clear": "Borrar",
    "strip.suggested": "Sugerido",

    "cat.favorites": "Favoritos",
    "cat.most_used": "Más usadas",
    "cat.add_category": "Añadir categoría",

    "settings.title": "ajustes",
    "settings.profile": "Perfil",
    "settings.name": "Nombre",
    "settings.avatar": "Avatar",
    "settings.color": "Color",
    "settings.voice": "Voz",
    "settings.voice.preview": "Probar voz",
    "settings.voice.speed": "Velocidad",
    "settings.voice.pitch": "Tono",
    "settings.tile_size": "Tamaño de tarjeta",
    "settings.language": "Idioma y voz",
    "settings.language.help": "Elige el idioma de los botones y la voz usada al leer las tarjetas. Las etiquetas de las tarjetas se mantienen tal como las escribiste.",
    "settings.pin.title": "Bloqueo del cuidador",
    "settings.pin.help": "Pon un PIN de 4 a 6 dígitos para que solo tú puedas entrar en modo Editar.",
    "settings.pin.set": "Crear PIN",
    "settings.pin.change": "Cambiar PIN",
    "settings.pin.remove": "Quitar PIN",
    "settings.ai.title": "Sugerencias con IA",
    "settings.ai.help": "Muestra sugerencias inteligentes encima de la frase, con Gemini 3 Flash.",
    "settings.ai.toggle": "Activar sugerencias con IA",
    "settings.backup.title": "Copia de seguridad",
    "settings.backup.help": "Descarga todo el tablero (tarjetas, fotos, grabaciones) en un solo archivo JSON.",
    "settings.backup.export": "Exportar este tablero",
    "settings.backup.import": "Importar tablero",
    "settings.reset.title": "Reiniciar tablero",
    "settings.reset.help": "Borra este tablero y empieza con el vocabulario inicial más reciente.",
    "settings.reset.button": "Restaurar vocabulario por defecto",
    "settings.save": "Guardar cambios",
    "settings.saved": "Guardado ✓",
    "settings.donate.title": "Paga por adelantado",
    "settings.donate.help": "Apadrina a una familia que no puede pagar un plan de pago. Reserva ahora — te avisaremos cuando el pago esté listo y podrás confirmar o cancelar.",
    "settings.donate.email": "Tu email",
    "settings.donate.name": "Tu nombre (opcional)",
    "settings.donate.amount": "Cantidad (USD)",
    "settings.donate.families": "Familias a apadrinar",
    "settings.donate.message": "Mensaje para las familias (opcional)",
    "settings.donate.submit": "Reservar apoyo",
    "settings.donate.thanks": "¡Gracias! Te avisaremos cuando el pago esté listo.",
    "settings.donate.stat": "{count} apoyos · ${pledged} comprometidos · {families} familias cubiertas",

    "common.back": "Atrás",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",

    "public.nonus.line1": "¿Estás fuera de los EE. UU.?",
    "public.nonus.line2": "SonSpeak se lanza primero en EE. UU. Apúntate a la lista de espera y te avisaremos en cuanto abramos en tu país.",
    "public.nonus.cta": "Apuntarme a la lista →",
    "public.nonus.dismiss": "Cerrar",
    "public.lang.label": "Idioma",
    "public.waitlist.title": "Lista de espera internacional",
    "public.waitlist.subtitle": "SonSpeak se lanza primero en EE. UU. mientras terminamos el trabajo de privacidad para cada región. Déjanos tu correo y te avisaremos el día que abramos en tu país.",
    "public.waitlist.name": "Tu nombre",
    "public.waitlist.email": "Correo electrónico",
    "public.waitlist.country": "País",
    "public.waitlist.submit": "Apuntarme a la lista",
    "public.waitlist.busy": "Apuntándote…",
    "public.waitlist.success.title": "¡Estás en la lista!",
    "public.waitlist.success.body": "Te enviaremos un correo a {email} en cuanto SonSpeak abra en tu país. Sin spam — solo el anuncio de lanzamiento.",
    "public.waitlist.success.home": "Volver a inicio",
    "public.waitlist.privacy_note": "Un solo correo cuando lancemos en tu país. Nada más, nunca.",
    "public.whatsnew.badge": "Novedades",
    "public.whatsnew.title": "Novedades en SonSpeak",
    "public.whatsnew.subtitle": "Lo más reciente que hemos lanzado. Nada se queda sin terminar.",
  },
  fr: {
    "board.tap_hint": "Touche une carte pour parler · Construis une phrase ci-dessus",
    "board.empty.favorites": "Marque des cartes en favori pour les voir ici.",
    "board.empty.most_used": "Touche des cartes et les plus utilisées apparaîtront ici.",
    "board.empty.category": "Touche Éditer en haut à droite pour ajouter des cartes.",
    "board.empty.title": "Rien ici pour l'instant",
    "board.add_tile": "Ajouter une carte",
    "board.edit": "Éditer",
    "board.done": "OK",
    "board.first_word.toast": "🎉 Premier mot ! Bravo !",
    "board.first_word.speak": "Oui ! C'était génial !",

    "strip.placeholder": "Touche une carte pour commencer une phrase…",
    "strip.speak": "Parler",
    "strip.back": "Retour",
    "strip.clear": "Effacer",
    "strip.suggested": "Suggéré",

    "cat.favorites": "Favoris",
    "cat.most_used": "Plus utilisées",
    "cat.add_category": "Ajouter une catégorie",

    "settings.title": "paramètres",
    "settings.profile": "Profil",
    "settings.name": "Nom",
    "settings.avatar": "Avatar",
    "settings.color": "Couleur",
    "settings.voice": "Voix",
    "settings.voice.preview": "Tester la voix",
    "settings.voice.speed": "Vitesse",
    "settings.voice.pitch": "Hauteur",
    "settings.tile_size": "Taille des cartes",
    "settings.language": "Langue et voix",
    "settings.language.help": "Choisis la langue des boutons et la voix utilisée pour lire les cartes. Les libellés des cartes restent comme tu les as écrits.",
    "settings.pin.title": "Verrou parental",
    "settings.pin.help": "Définis un PIN de 4 à 6 chiffres pour entrer en mode Édition.",
    "settings.pin.set": "Créer un PIN",
    "settings.pin.change": "Changer le PIN",
    "settings.pin.remove": "Supprimer le PIN",
    "settings.ai.title": "Suggestions IA",
    "settings.ai.help": "Affiche des suggestions intelligentes au-dessus de la phrase, propulsées par Gemini 3 Flash.",
    "settings.ai.toggle": "Activer les suggestions IA",
    "settings.backup.title": "Sauvegarde et restauration",
    "settings.backup.help": "Télécharge tout le tableau (cartes, photos, enregistrements) dans un seul fichier JSON.",
    "settings.backup.export": "Exporter ce tableau",
    "settings.backup.import": "Importer un tableau",
    "settings.reset.title": "Réinitialiser le tableau",
    "settings.reset.help": "Efface ce tableau et recommence avec le vocabulaire de départ.",
    "settings.reset.button": "Restaurer le vocabulaire par défaut",
    "settings.save": "Enregistrer",
    "settings.saved": "Enregistré ✓",
    "settings.donate.title": "Paie au suivant",
    "settings.donate.help": "Parraine une famille qui n'a pas les moyens d'un plan payant. Pré-réserve maintenant — on t'écrit dès que le paiement est en ligne, libre à toi de confirmer ou non.",
    "settings.donate.email": "Ton email",
    "settings.donate.name": "Ton nom (optionnel)",
    "settings.donate.amount": "Montant (USD)",
    "settings.donate.families": "Familles à parrainer",
    "settings.donate.message": "Message pour les familles (optionnel)",
    "settings.donate.submit": "Pré-réserver",
    "settings.donate.thanks": "Merci ! On te recontacte dès que le paiement est en ligne.",
    "settings.donate.stat": "{count} soutiens · ${pledged} promis · {families} familles couvertes",

    "common.back": "Retour",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",

    "public.nonus.line1": "Hors des États-Unis ?",
    "public.nonus.line2": "SonSpeak se lance d'abord aux États-Unis. Inscris-toi à la liste d'attente et on te préviendra dès qu'on ouvre dans ton pays.",
    "public.nonus.cta": "Rejoindre la liste →",
    "public.nonus.dismiss": "Fermer",
    "public.lang.label": "Langue",
    "public.waitlist.title": "Liste d'attente internationale",
    "public.waitlist.subtitle": "SonSpeak se lance d'abord aux États-Unis pendant qu'on finit le travail juridique propre à chaque région. Laisse-nous ton email — on te préviendra le jour où on ouvre dans ton pays.",
    "public.waitlist.name": "Ton nom",
    "public.waitlist.email": "Email",
    "public.waitlist.country": "Pays",
    "public.waitlist.submit": "Ajoute-moi à la liste",
    "public.waitlist.busy": "Inscription en cours…",
    "public.waitlist.success.title": "Tu es sur la liste !",
    "public.waitlist.success.body": "On enverra un email à {email} dès que SonSpeak ouvre dans ton pays. Pas de spam — juste l'annonce du lancement.",
    "public.waitlist.success.home": "Retour à l'accueil",
    "public.waitlist.privacy_note": "Un seul email quand on lance dans ton pays. Rien d'autre, jamais.",
    "public.whatsnew.badge": "Nouveautés",
    "public.whatsnew.title": "Nouveautés sur SonSpeak",
    "public.whatsnew.subtitle": "Les dernières mises à jour livrées. Rien n'est laissé en plan.",
  },
};

export function t(code, key, vars = {}) {
  const lang = STRINGS[code] || STRINGS.en;
  let str = lang[key] || STRINGS.en[key] || key;
  for (const [k, v] of Object.entries(vars)) {
    str = str.replaceAll(`{${k}}`, String(v));
  }
  return str;
}
